import { useCallback, useState, ReactNode } from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import ConfirmDialog from './ConfirmDialog';
import { adStatusService, AdEntityType, StatusChangeResult } from '../services/ad-status.service';
import { useAuthStore } from '../stores/auth.store';

export const NOUN: Record<AdEntityType, [string, string]> = {
  campaign: ['campaign', 'campaigns'],
  adset: ['ad set', 'ad sets'],
  ad: ['ad', 'ads'],
};

/** The endpoint is admin-only, and a control that answers 403 is worse than no control. */
export function useMayChangeStatus(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'admin' || role === 'media_buyer';
}

/**
 * Asking, doing, and reporting what came back — shared so a row toggle and a bulk button
 * cannot end up meaning different things.
 */
export function useAdStatus(entityType: AdEntityType, invalidateKey: string, onDone?: () => void) {
  const [pending, setPending] = useState<
    { ids: string[]; status: 'ACTIVE' | 'PAUSED'; already: number } | null>(null);
  // The direction travels with the failures: `pending` is cleared before they are shown, so
  // a retry that read it back there would resume what you asked to pause.
  const [failed, setFailed] = useState<
    { status: 'ACTIVE' | 'PAUSED'; asked: number; rows: StatusChangeResult['failed'] } | null>(null);
  const queryClient = useQueryClient();
  const [one, many] = NOUN[entityType];

  const run = useMutation({
    mutationFn: ({ ids, status }: NonNullable<typeof pending>) =>
      adStatusService.setStatus(entityType, ids, status),
    onSuccess: (res, { ids, status }) => {
      queryClient.invalidateQueries({ queryKey: [invalidateKey] });
      setPending(null);
      if (res.failed.length) {
        setFailed({ status, asked: ids.length, rows: res.failed });
      } else {
        enqueueSnackbar(`${status === 'PAUSED' ? 'Paused' : 'Resumed'} ${res.succeeded.length} ${
          res.succeeded.length === 1 ? one : many}`, { variant: 'success' });
        onDone?.();
      }
    },
    onError: (err: unknown) => {
      setPending(null);
      enqueueSnackbar(err instanceof Error ? err.message : 'The change was refused', { variant: 'error' });
    },
  });

  const retry = useMutation({
    mutationFn: (f: NonNullable<typeof failed>) =>
      adStatusService.setStatus(entityType, f.rows.map((r) => r.id), f.status),
    onSuccess: (res, f) => {
      queryClient.invalidateQueries({ queryKey: [invalidateKey] });
      setFailed(res.failed.length ? { ...f, asked: f.rows.length, rows: res.failed } : null);
      if (!res.failed.length) onDone?.();
    },
  });

  const count = pending?.ids.length ?? 0;
  const noun = count === 1 ? one : many;

  const dialogs: ReactNode = (
    <>
      <ConfirmDialog
        open={!!pending}
        title={pending?.status === 'PAUSED' ? `Pause ${count} ${noun}?` : `Resume ${count} ${noun}?`}
        destructive={pending?.status === 'PAUSED'}
        body={
          <Box>
            <Typography>
              {pending?.status === 'PAUSED'
                ? 'This stops delivery on the ad platform, not just in Neptune.'
                : 'This resumes delivery on the ad platform.'}
            </Typography>
            {!!pending?.already && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {pending.already} of them {pending.already === 1 ? 'is' : 'are'} already
                {pending.status === 'PAUSED' ? ' paused' : ' active'}.
              </Typography>
            )}
          </Box>
        }
        confirmLabel={pending?.status === 'PAUSED' ? `Pause ${count}` : `Resume ${count}`}
        busyLabel={pending?.status === 'PAUSED' ? 'Pausing...' : 'Resuming...'}
        busy={run.isPending}
        onConfirm={() => pending && run.mutate(pending)}
        onClose={() => setPending(null)}
      />

      <ConfirmDialog
        open={!!failed}
        title={`${failed?.rows.length ?? 0} of ${failed?.asked ?? 0} could not be ${
          failed?.status === 'PAUSED' ? 'paused' : 'resumed'}`}
        body={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {(failed?.rows.length ?? 0) >= (failed?.asked ?? 0)
                ? 'Nothing was changed.'
                : 'The rest went through and are left as they are.'}
            </Typography>
            <List dense>
              {(failed?.rows ?? []).map((f) => (
                <ListItem key={f.id} disableGutters>
                  <ListItemText primary={f.name} secondary={f.error} />
                </ListItem>
              ))}
            </List>
          </Box>
        }
        confirmLabel="Retry these"
        busy={retry.isPending}
        onConfirm={() => failed && retry.mutate(failed)}
        onClose={() => { setFailed(null); onDone?.(); }}
      />
    </>
  );

  // `already` lets the dialog say the change is smaller. Stable, so a memoised grid column
  // can hang off it without rebuilding every render.
  const ask = useCallback(
    (ids: string[], status: 'ACTIVE' | 'PAUSED', already = 0) => setPending({ ids, status, already }),
    []);

  return { ask, dialogs };
}
